import { useEffect, useState } from "react";
import api, { formatApiError, ONEGO_LOGO } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Plus, Shield, Truck, CheckCircle2, Package, Send, XCircle } from "lucide-react";
import { toast } from "sonner";

function fmt(n) {
    return new Intl.NumberFormat("en-KE", { maximumFractionDigits: 0 }).format(n || 0);
}

const CATS = ["FMCG", "Beverage", "Household", "Electronics", "Solar", "Bakery", "Stationery"];

const NEXT_STATUS = {
    pending: { code: "approved", label: "Approve", icon: CheckCircle2, color: "bg-emerald-500" },
    approved: { code: "packed", label: "Pack", icon: Package, color: "bg-cyan-500" },
    packed: { code: "dispatched", label: "Dispatch", icon: Send, color: "bg-amber-500" },
    dispatched: { code: "delivered", label: "Mark delivered", icon: Truck, color: "bg-onego-navy" },
};

const STATUS_CHIP = {
    pending: "bg-amber-100 text-amber-700",
    approved: "bg-cyan-100 text-cyan-700",
    packed: "bg-violet-100 text-violet-700",
    dispatched: "bg-blue-100 text-blue-700",
    delivered: "bg-emerald-100 text-emerald-700",
    cancelled: "bg-rose-100 text-rose-700",
};

export default function SupplierPortal() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [products, setProducts] = useState([]);
    const [orders, setOrders] = useState([]);
    const [form, setForm] = useState({ name: "", category: "FMCG", supplier: "", price: 100, unit: "unit" });
    const [loading, setLoading] = useState(false);
    const [updating, setUpdating] = useState(null);

    const advance = async (orderId, nextStatus) => {
        setUpdating(orderId);
        try {
            await api.post(`/supplier/orders/${orderId}/status`, { status: nextStatus });
            toast.success(`Order moved to ${nextStatus}`);
            await load();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || err.message);
        } finally {
            setUpdating(null);
        }
    };

    const load = async () => {
        const [p, o] = await Promise.all([
            api.get("/supplier/products"),
            api.get("/supplier/orders"),
        ]);
        setProducts(p.data);
        setOrders(o.data);
    };

    useEffect(() => { load().catch(() => {}); }, []);

    const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/supplier/products", { ...form, price: Number(form.price) });
            setForm({ name: "", category: "FMCG", supplier: form.supplier, price: 100, unit: "unit" });
            toast.success("Product listed");
            await load();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    if (user && !["supplier", "admin"].includes(user.role)) {
        return (
            <div className="min-h-screen flex items-center justify-center p-6 text-center" data-testid="supplier-forbidden">
                <div className="og-card p-10 max-w-md">
                    <Shield size={32} className="mx-auto text-rose-500" />
                    <h1 className="mt-4 font-display text-2xl font-bold text-onego-navy">Suppliers only</h1>
                    <p className="mt-2 text-slate-600">Sign in with a supplier account to access the portal.</p>
                    <Link to="/login" className="og-btn-gradient inline-flex mt-5">Sign in</Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-onego-bg" data-testid="supplier-portal">
            <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
                <div className="px-6 lg:px-10 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3">
                        <img src={ONEGO_LOGO} alt="OneGo" className="h-8" />
                        <span className="og-chip">Supplier</span>
                    </Link>
                    <button onClick={async () => { await logout(); navigate("/"); }} className="og-btn-ghost inline-flex items-center gap-2" data-testid="supplier-logout">
                        <LogOut size={16} /> Sign out
                    </button>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 lg:px-10 py-8 space-y-8">
                <h1 className="font-display text-4xl font-extrabold text-onego-navy">Supplier portal</h1>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="og-card p-6">
                        <p className="text-xs uppercase tracking-widest font-semibold text-slate-500">Listed products</p>
                        <p className="mt-3 font-display text-3xl font-extrabold text-onego-navy">{products.length}</p>
                    </div>
                    <div className="og-card p-6">
                        <p className="text-xs uppercase tracking-widest font-semibold text-slate-500">Network orders</p>
                        <p className="mt-3 font-display text-3xl font-extrabold text-onego-navy">{orders.length}</p>
                    </div>
                    <div className="og-card p-6">
                        <p className="text-xs uppercase tracking-widest font-semibold text-slate-500">Order value</p>
                        <p className="mt-3 font-display text-3xl font-extrabold text-onego-navy">KES {fmt(orders.reduce((a, o) => a + (o.total || 0), 0))}</p>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <form onSubmit={submit} className="og-card p-7" data-testid="supplier-product-form">
                        <h3 className="font-display text-xl font-bold text-onego-navy flex items-center gap-2"><Plus size={18} /> List a product</h3>
                        <div className="mt-3 space-y-3">
                            <input className="og-input" required placeholder="Product name" value={form.name} onChange={upd("name")} data-testid="supplier-product-name" />
                            <input className="og-input" required placeholder="Supplier / brand" value={form.supplier} onChange={upd("supplier")} data-testid="supplier-product-supplier" />
                            <select className="og-input" value={form.category} onChange={upd("category")} data-testid="supplier-product-category">
                                {CATS.map((c) => <option key={c}>{c}</option>)}
                            </select>
                            <div className="grid grid-cols-2 gap-3">
                                <input className="og-input" type="number" min={1} value={form.price} onChange={upd("price")} placeholder="Price" data-testid="supplier-product-price" />
                                <input className="og-input" value={form.unit} onChange={upd("unit")} placeholder="Unit (pack, crate)" data-testid="supplier-product-unit" />
                            </div>
                            <button type="submit" disabled={loading} className="og-btn-gradient w-full disabled:opacity-60" data-testid="supplier-product-submit">
                                {loading ? "Listing\u2026" : "List product"}
                            </button>
                        </div>
                    </form>

                    <div className="lg:col-span-2 og-card p-7">
                        <h3 className="font-display text-xl font-bold text-onego-navy">Your catalog</h3>
                        <div className="mt-3 divide-y divide-slate-100">
                            {products.length === 0 && <p className="py-6 text-sm text-slate-500">No products yet. Add your first one.</p>}
                            {products.map((p) => (
                                <div key={p.id} className="py-3 flex items-center justify-between" data-testid={`supplier-product-row-${p.id}`}>
                                    <div>
                                        <p className="font-bold text-onego-navy">{p.name}</p>
                                        <p className="text-xs text-slate-500">{p.supplier} &middot; {p.category} &middot; per {p.unit}</p>
                                    </div>
                                    <p className="font-display font-bold text-onego-navy">KES {fmt(p.price)}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="og-card p-7">
                    <h3 className="font-display text-xl font-bold text-onego-navy flex items-center gap-2"><Truck size={18} /> Network orders &mdash; fulfilment</h3>
                    <p className="text-sm text-slate-500 mt-1">Move orders through the workflow: pending &rarr; approved &rarr; packed &rarr; dispatched &rarr; delivered.</p>
                    <div className="mt-4 space-y-3 max-h-[520px] overflow-auto">
                        {orders.length === 0 && <p className="py-4 text-sm text-slate-500">No orders yet.</p>}
                        {orders.map((o) => {
                            const next = NEXT_STATUS[o.status];
                            const NextIcon = next?.icon;
                            return (
                                <div key={o.id} className="og-card p-4" data-testid={`supplier-order-${o.id}`}>
                                    <div className="flex items-center justify-between flex-wrap gap-2">
                                        <div>
                                            <p className="font-bold text-onego-navy">Order {o.id.slice(0, 8).toUpperCase()}</p>
                                            <p className="text-xs text-slate-500">{new Date(o.created_at).toLocaleString()} &middot; {o.items?.length || 0} items &middot; KES {fmt(o.total)} &middot; {o.region || "urban"}</p>
                                            {o.delivery_address && (
                                                <p className="text-xs text-slate-500 mt-0.5">{o.delivery_address}</p>
                                            )}
                                        </div>
                                        <span className={`og-chip ${STATUS_CHIP[o.status] || ""}`} data-testid={`supplier-order-status-${o.id}`}>{o.status}</span>
                                    </div>
                                    <div className="mt-3 flex flex-wrap gap-2">
                                        {next && (
                                            <button
                                                onClick={() => advance(o.id, next.code)}
                                                disabled={updating === o.id}
                                                className="og-btn-gradient inline-flex items-center gap-2 text-sm disabled:opacity-60"
                                                data-testid={`supplier-advance-${o.id}`}
                                            >
                                                <NextIcon size={14} /> {next.label}
                                            </button>
                                        )}
                                        {o.status !== "delivered" && o.status !== "cancelled" && (
                                            <button
                                                onClick={() => advance(o.id, "cancelled")}
                                                disabled={updating === o.id}
                                                className="og-btn-ghost inline-flex items-center gap-1 text-sm text-rose-600 disabled:opacity-60"
                                                data-testid={`supplier-cancel-${o.id}`}
                                            >
                                                <XCircle size={14} /> Cancel
                                            </button>
                                        )}
                                    </div>
                                    {(o.fulfilment_log || []).length > 0 && (
                                        <details className="mt-3" data-testid={`supplier-log-${o.id}`}>
                                            <summary className="text-xs font-semibold text-slate-500 cursor-pointer">Fulfilment log</summary>
                                            <ol className="mt-2 space-y-1">
                                                {o.fulfilment_log.map((l, idx) => (
                                                    <li key={idx} className="text-xs text-slate-500">
                                                        <span className="font-semibold text-onego-navy">{l.status}</span> &middot; {new Date(l.at).toLocaleString()} &middot; by {l.actor}
                                                    </li>
                                                ))}
                                            </ol>
                                        </details>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </main>
        </div>
    );
}
